import { toPlainText } from './html'
import { resolveImageSrc, shouldProxyImage } from './image'

// photo.src is a CMS path, not a public url. imageCDN() resolves it but calls
// useRuntimeConfig() internally, and schemas are serialised lazily by useHead —
// outside the Nuxt instance context, which throws. Callers resolve the base url
// eagerly and pass it in.
export const schemaImageUrl = (src: string | undefined | null, assetBaseUrl: string): string => {
    if (!src) return ''
    if (!shouldProxyImage(src)) return src
    return `${assetBaseUrl.replace(/\/$/, '')}/${resolveImageSrc(src)}`
}

export const schemaLanguage = (locale: string, country: string): string =>
    `${locale.toLowerCase()}-${country.toUpperCase()}`

// Canonicals and the sitemap always carry the trailing slash; schema urls must match
// them exactly, and localePath('/') drops it.
export const schemaUrl = (path: string, base: string): string => {
    try {
        const url = new URL(path, base)
        if (!url.pathname.endsWith('/')) url.pathname = `${url.pathname}/`
        return url.toString()
    } catch {
        return ''
    }
}

export const schemaVideoObject = (video: {
    name?: string | null
    description?: string | null
    thumbnailUrl?: string | null
    contentUrl?: string | null
    embedUrl?: string | null
    // Unix seconds of the uploaded file (CMS `mtime`); the videos carry no editorial date.
    uploadedAt?: number | null
}) => {
    if (!video.name || !video.thumbnailUrl || !video.contentUrl) return null

    return {
        '@type': 'VideoObject',
        name: video.name,
        ...(video.description ? { description: video.description } : {}),
        thumbnailUrl: video.thumbnailUrl,
        ...(video.uploadedAt ? { uploadDate: new Date(video.uploadedAt * 1000).toISOString().slice(0, 10) } : {}),
        contentUrl: video.contentUrl,
        ...(video.embedUrl ? { embedUrl: video.embedUrl } : {}),
    }
}

export const SCHEMA_ORGANIZATION_NAME = 'Nostra'

// Visu triju rinku porasteje: "UAB NOSTRA".
export const SCHEMA_ORGANIZATION_LEGAL_NAME = 'UAB NOSTRA'

// Kontaktai teikiami per `contactPoint`, o ne `address`/`telephone` sakneje: is pastaruju
// Google padaro vietos verslo isvada (Local businesses), o fizines parduotuves nera.
// Toks pat sprendimas apple.com, nike.com ir pigu.lt.
export const schemaOrganization = (
    origin: string,
    sameAs: string[],
    contact?: { phone?: string; email?: string; country?: string },
) => ({
    '@type': 'Organization',
    '@id': `${origin}/#organization`,
    name: SCHEMA_ORGANIZATION_NAME,
    legalName: SCHEMA_ORGANIZATION_LEGAL_NAME,
    url: `${origin}/`,
    logo: `${origin}/img/logo-schema.png`,
    image: `${origin}/img/logo-schema.png`,
    ...(contact?.phone || contact?.email
        ? {
            contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                ...(contact.phone ? { telephone: contact.phone } : {}),
                ...(contact.email ? { email: contact.email } : {}),
                ...(contact.country ? { areaServed: contact.country.toUpperCase() } : {}),
            },
        }
        : {}),
    ...(sameAs.length ? { sameAs } : {}),
})

export const schemaBreadcrumbList = (
    items: { name: string; url: string }[],
    home: { name: string; url: string },
) => {
    const trail = [home, ...items].filter(item => !!item.url)
    const current = trail.at(-1)

    return {
        '@type': 'BreadcrumbList',
        ...(current ? { name: toPlainText(current.name) } : {}),
        itemListElement: trail.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: toPlainText(item.name),
            item: item.url,
        })),
    }
}
